import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Delete } from '@mui/icons-material';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import {
  apiErrorMessage,
  BookingExtra,
  RuteroPassengerPayload,
  useAddExtra,
  useBookingExtras,
  useRemoveExtra,
} from '../queries';
import type { BookingMoment } from '../bookingMoment';
import type { ConnectorCapabilityVerdict } from '../connectorCapabilityResolution';
import {
  addExtraPayload,
  emptyExtraDrafts,
  extraAddBlock,
  extraDateBounds,
  extraDatePickerVisible,
  extraDraftsFor,
  extraMaxAllowed,
  extraPassengerOptions,
  extraPriceNote,
  extraRemainingAllowance,
  extrasLegGroups,
  partitionExtraAvailability,
  extrasSectionVisible,
  extraValidityLabel,
  passengerSelectorVisible,
  purchasedExtraRows,
  purchasedExtraOrigins,
  resolveExtraPassengers,
} from '../bookingExtras';
import type { ExtraDrafts, PurchasedExtraIdentity } from '../bookingExtras';
import { formatMoney } from '../money';

/** El rótulo del tramo dueño: las mismas claves que el modal y las opciones de la oferta. */
const LEG_LABEL_KEY: Record<'outbound' | 'return', string> = {
  outbound: 'travelServices.booking.outbound',
  return: 'travelServices.booking.return',
};

interface Props {
  bookingId: string;
  /** Qué dice el conector de ESTA reserva sobre `extras` (fail-closed). */
  verdict: ConnectorCapabilityVerdict;
  /** El momento de la reserva: los extras se compran sobre una ya creada. */
  moment: BookingMoment;
  /** Hay reserva del otro lado (sin id remoto no hay nada que operar). */
  hasRemoteBooking: boolean;
  /**
   * Los pasajeros del REGISTRO, en el orden en que viajaron al hold: su
   * posición es el índice 0-based que el contrato espera en `passengers[]`.
   */
  passengers?: RuteroPassengerPayload[];
}

/**
 * Servicios adicionales de una reserva (excursiones, paquetes opcionales,
 * sillas de bebé). Se operan sobre la reserva YA creada — incluso en hold —,
 * no en el momento de reservar: el hold no tiene canal para pedirlos.
 *
 * La MISMA sección en las tres superficies que la muestran (el modal del flujo
 * en Reservado y en Confirmado, y el detalle del listado), y con la misma
 * condición para existir: la decide `extrasSectionVisible` acá adentro, no
 * cada caller, así que ninguna puede ofrecer lo que otra oculta.
 *
 * Un extra se le compra a PASAJEROS, no a la reserva: con más de uno se elige
 * a quiénes (todos por default) y el payload lleva exactamente esos.
 */
export function BookingExtras({
  bookingId,
  verdict,
  moment,
  hasRemoteBooking,
  passengers,
}: Props) {
  const { t, i18n } = useTranslation();
  const { addToast } = useSnackBar();
  const visible = extrasSectionVisible({ verdict, moment, hasRemoteBooking });
  const { data, isLoading, isError, error } = useBookingExtras(bookingId, visible);
  const addExtra = useAddExtra(bookingId);
  const removeExtra = useRemoveExtra(bookingId);
  /**
   * La fecha elegida y a quiénes se les compra cada extra, ATADAS a la reserva
   * que las produjo: las dos pantallas cambian de reserva sin desmontarse, y
   * los `extraId` son del proveedor —el mismo id vive en dos reservas suyas—,
   * así que un borrador que sobreviva compraría la excursión para los
   * pasajeros elegidos en otra. Ausente = nadie tocó los chips = TODOS.
   */
  const [draft, setDraft] = useState<ExtraDrafts>(() => emptyExtraDrafts(bookingId));
  const drafts = extraDraftsFor(draft, bookingId);

  const options = extraPassengerOptions(passengers);
  const showsPicker = passengerSelectorVisible(options);

  if (!visible) return null;

  const passengersFor = (extraId: string) =>
    resolveExtraPassengers({ options, chosen: drafts.passengers[extraId] });

  const setDate = (extraId: string, date: string) =>
    setDraft((prev) => {
      const base = extraDraftsFor(prev, bookingId);
      return { ...base, dates: { ...base.dates, [extraId]: date } };
    });

  const togglePassenger = (extraId: string, index: number) =>
    setDraft((prev) => {
      const base = extraDraftsFor(prev, bookingId);
      const current = resolveExtraPassengers({
        options,
        chosen: base.passengers[extraId],
      });
      return {
        ...base,
        passengers: {
          ...base.passengers,
          [extraId]: current.includes(index)
            ? current.filter((chosen) => chosen !== index)
            : [...current, index],
        },
      };
    });

  const handleAdd = async (extra: BookingExtra) => {
    try {
      await addExtra.mutateAsync(
        addExtraPayload({
          extra,
          date: drafts.dates[extra.extraId],
          passengers: passengersFor(extra.extraId),
          options,
        }),
      );
      addToast({ message: t('travelServices.extras.added'), color: 'success' });
    } catch (mutationError) {
      addToast({
        message: apiErrorMessage(mutationError) ?? t('travelServices.extras.addError'),
        color: 'error',
      });
    }
  };

  const handleRemove = async (purchaseId: string) => {
    try {
      await removeExtra.mutateAsync(purchaseId);
      addToast({ message: t('travelServices.extras.removed'), color: 'success' });
    } catch (mutationError) {
      addToast({
        message: apiErrorMessage(mutationError) ?? t('travelServices.extras.removeError'),
        color: 'error',
      });
    }
  };

  const passengerLabel = (index: number) =>
    options[index]?.name ??
    t('travelServices.extras.passengerFallback', { number: index + 1 });

  /** De quién es una compra ya hecha, con lo que el proveedor haya dado. */
  const identityLabel = (identity: PurchasedExtraIdentity) => {
    switch (identity.kind) {
    case 'passenger':
      return passengerLabel(identity.index);
    case 'reference':
      return t('travelServices.extras.providerPassenger', {
        reference: identity.reference,
      });
    case 'unspecified':
      return t('travelServices.extras.passengerUnknown');
    default:
      return '';
    }
  };

  const busy = addExtra.isLoading || removeExtra.isLoading;
  const groups = data
    ? extrasLegGroups({
      available: data.available,
      purchased: data.purchased,
    }).map((group) => ({
      ...group,
      availability: partitionExtraAvailability({
        available: group.available,
        purchased: group.purchased,
      }),
    }))
    : [];
  const purchasedOrigins = purchasedExtraOrigins({
    purchased: data?.purchased ?? [],
  });

  return (
    <Box mt={2}>
      <Divider sx={{ mb: 1.5 }} />
      <Typography variant="subtitle2" gutterBottom>
        {t('travelServices.extras.title')}
      </Typography>

      {isLoading && (
        <Typography variant="body2" color="text.secondary">
          {t('travelServices.extras.loading')}
        </Typography>
      )}
      {isError && (
        <Alert severity="info">
          {apiErrorMessage(error) ?? t('travelServices.extras.unavailable')}
        </Alert>
      )}

      {data && data.available.length === 0 && data.purchased.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('travelServices.extras.none')}
        </Typography>
      )}

      {/* Por TRAMO dueño: en transfers el catálogo es por tramo (el mismo
          servicio una vez por tramo, con su propio extraId) y sin el rótulo
          Ida/Vuelta el asesor no sabe a cuál le agrega, ni de cuál tramo es
          lo ya comprado. Un proveedor sin tramos = un único grupo sin rótulo,
          el layout de siempre. */}
      {data &&
        groups.map((group) => (
          // La clave del grupo sin tramo es '': un legId real nunca es ''
          // (se trimea a null), así que no puede chocar con la de un tramo.
          <Box key={group.legId ?? ''} mb={1.5}>
            {group.direction && (
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                {t(LEG_LABEL_KEY[group.direction])}
              </Typography>
            )}

            {group.purchased.length > 0 && (
              <Box mb={1}>
                <Typography variant="caption" color="text.secondary">
                  {t('travelServices.extras.purchased')}
                </Typography>
                <Stack spacing={0.5} mt={0.5}>
                  {/* La identidad del pasajero y el botón que cancela salen de la
                MISMA fila: una compra es cancelable, y sin decir de quién es
                cada una el botón borraría la de cualquiera. */}
                  {purchasedExtraRows({ purchased: group.purchased, options }).map(
                    ({ purchase, identity }) => {
                      const who = identityLabel(identity);
                      const origin = purchasedOrigins.get(purchase.purchaseId);
                      return (
                        <Box
                          key={purchase.purchaseId}
                          display="flex"
                          alignItems="center"
                          gap={1}
                          px={1.5}
                          py={0.75}
                          bgcolor="action.selected"
                          borderRadius={1}
                        >
                          <Box flex={1} minWidth={0}>
                            <Typography variant="body2">
                              {purchase.name ?? purchase.extraId}
                            </Typography>
                            {who && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {who}
                              </Typography>
                            )}
                            {purchase.date && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {purchase.date}
                              </Typography>
                            )}
                            {origin?.providerAdded && (
                              <Chip
                                size="small"
                                variant="outlined"
                                color="info"
                                sx={{ mt: 0.5 }}
                                label={t('travelServices.booking.confirmedOptionalItems.providerAdded')}
                              />
                            )}
                          </Box>
                          {origin?.free ? (
                            <Typography variant="body2" color="success.main">
                              {t('travelServices.booking.confirmedOptionalItems.free')}
                            </Typography>
                          ) : purchase.price ? (
                            <Typography variant="body2">{formatMoney(purchase.price)}</Typography>
                          ) : null}
                          <Tooltip title={t('travelServices.extras.remove')}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={busy}
                                onClick={() => handleRemove(purchase.purchaseId)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      );
                    },
                  )}
                </Stack>
              </Box>
            )}

            {group.availability.actionable.length > 0 && (
              <Stack spacing={0.5}>
                {group.availability.actionable.map((extra) => {
                  const chosen = passengersFor(extra.extraId);
                  // Lo que impide comprar y el motivo que se lee al lado del botón
                  // salen de la MISMA expresión: nunca un gris mudo.
                  const block = extraAddBlock({
                    extra,
                    date: drafts.dates[extra.extraId],
                    passengers: chosen,
                    options,
                    purchased: group.purchased,
                  });
                  const priceNote = extraPriceNote({ options, passengers: chosen });
                  const bounds = extraDateBounds(extra);
                  const validity = extraValidityLabel(extra, i18n.language);
                  // El cupo que se dibuja y el que bloquea son la MISMA cuenta.
                  const maxAllowed = extraMaxAllowed(extra);
                  const remaining = extraRemainingAllowance({
                    extra,
                    purchased: group.purchased,
                  });
                  return (
                    <Box
                      key={extra.extraId}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      px={1.5}
                      py={0.75}
                      bgcolor="grey.100"
                      borderRadius={1}
                      flexWrap="wrap"
                    >
                      <Box flex={1} minWidth={180}>
                        <Typography variant="body2">{extra.name}</Typography>
                        {extra.description && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {extra.description}
                          </Typography>
                        )}
                        {validity && (
                          <Chip
                            size="small"
                            variant="outlined"
                            sx={{ mt: 0.5 }}
                            label={t('travelServices.extras.validRange', validity)}
                          />
                        )}
                        {maxAllowed !== null && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('travelServices.booking.optionalItems.maxAllowed', {
                              max: maxAllowed,
                            })}
                          </Typography>
                        )}
                        {/* A quiénes se les compra. Con un solo pasajero no hay nada
                      que elegir y el selector no existe. */}
                        {showsPicker && (
                          <Box mt={0.75}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t('travelServices.extras.passengers')}
                            </Typography>
                            <Stack direction="row" spacing={0.5} mt={0.25} flexWrap="wrap" useFlexGap>
                              {options.map((option) => {
                                const selected = chosen.includes(option.index);
                                return (
                                  <Chip
                                    key={option.index}
                                    size="small"
                                    label={passengerLabel(option.index)}
                                    color={selected ? 'primary' : 'default'}
                                    variant={selected ? 'filled' : 'outlined'}
                                    disabled={busy}
                                    onClick={() =>
                                      togglePassenger(extra.extraId, option.index)
                                    }
                                  />
                                );
                              })}
                            </Stack>
                          </Box>
                        )}
                      </Box>
                      {/* El selector existe SOLO si el extra declara la fecha
                    elegible (dateSelection optional/required): con 'none' el
                    servicio ya tiene fecha (la del tramo) y el POST la ignora.
                    Cuando existe, se acota a la vigencia Y la compra se
                    bloquea con su motivo, porque el input deja tipear fuera
                    del rango. */}
                      {extraDatePickerVisible(extra) && (
                        <TextField
                          size="small"
                          type="date"
                          label={t('travelServices.extras.serviceDate')}
                          value={drafts.dates[extra.extraId] ?? ''}
                          onChange={(event) => setDate(extra.extraId, event.target.value)}
                          inputProps={{ min: bounds.min, max: bounds.max }}
                          InputLabelProps={{ shrink: true }}
                          error={block === 'dateOutOfRange' || block === 'dateRequired'}
                          sx={{ width: 165 }}
                        />
                      )}
                      {extra.price && (
                        <Box textAlign="right">
                          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                            {formatMoney(extra.price)}
                          </Typography>
                          {/* Nada se multiplica acá: se dice qué describe el número
                        publicado y cuántos pasajeros lo llevan. El importe de
                        lo comprado lo devuelve el server. */}
                          {priceNote && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              {t('travelServices.extras.perPassenger', {
                                count: priceNote.passengers,
                              })}
                            </Typography>
                          )}
                        </Box>
                      )}
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <LoadingButton
                          size="small"
                          variant="outlined"
                          loading={addExtra.isLoading}
                          disabled={busy || block !== null}
                          onClick={() => handleAdd(extra)}
                        >
                          {t('travelServices.extras.add')}
                        </LoadingButton>
                        {block && (
                          <Typography variant="caption" color="text.secondary">
                            {t(`travelServices.extras.blocked.${block}`, {
                              max: maxAllowed,
                              remaining,
                            })}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
            {group.availability.unavailable.length > 0 && (
              <Box component="details" mt={1}>
                <Box
                  component="summary"
                  sx={{ cursor: 'pointer', color: 'text.secondary', typography: 'caption' }}
                >
                  {t('travelServices.extras.unavailableCount', {
                    count: group.availability.unavailable.length,
                  })}
                </Box>
                <Stack spacing={0.5} mt={0.5}>
                  {group.availability.unavailable.map((extra) => (
                    <Box
                      key={extra.extraId}
                      display="flex"
                      justifyContent="space-between"
                      gap={1}
                      px={1.5}
                      py={0.75}
                      bgcolor="grey.100"
                      borderRadius={1}
                    >
                      <Typography variant="body2">{extra.name}</Typography>
                      <Typography variant="caption" color="text.secondary" textAlign="right">
                        {t('travelServices.extras.blocked.limitReached')}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        ))}
    </Box>
  );
}
